import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '@mui/material';
import { PlatformEntity } from '@provision/entity-types';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { CustomAttributeGroup } from 'src/graphql/entities/custom-attributes/custom-attributes';
import { useGlobalStore } from 'src/store';
import { DraggableGroup } from './draggable-group';

export const DroppablePartner = ({
  groups,
  entity,
  partnerId,
}: {
  groups: Partial<CustomAttributeGroup>[];
  entity: PlatformEntity;
  partnerId: number;
}) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { control } =
    useFormContext<
      Record<string, Record<string, Partial<CustomAttributeGroup>[] | undefined>>
    >();

  const fieldArrayMethods = useFieldArray({
    control,
    name: `${entity.id}.${partnerId}`,
  });

  const hasAccessToCreate =
    selectedPartnerId === PLATFORM_PARTNER_ID || partnerId === selectedPartnerId;

  const attributeList = useMemo(
    () => groups?.map((group) => group.displayName).filter((name) => !!name) as string[],
    [groups],
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = fieldArrayMethods.fields.findIndex((group) => group.id === active.id);
        const newIndex = fieldArrayMethods.fields.findIndex((group) => group.id === over?.id);
        fieldArrayMethods.move(oldIndex, newIndex);
      }
    },
    [fieldArrayMethods],
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={attributeList} strategy={verticalListSortingStrategy}>
          {fieldArrayMethods.fields.map((field, index) => (
            <DraggableGroup
              group={field}
              entity={entity}
              key={field.id}
              index={index}
              fieldArrayMethods={fieldArrayMethods}
            />
          ))}
        </SortableContext>
      </DndContext>

      {hasAccessToCreate ? (
        <Button
          size="small"
          onClick={() =>
            fieldArrayMethods.append({
              isNew: true,
              isEditing: true,
              partnerId,
              entity: entity.id,
              displayName: '',
            })
          }
          sx={{
            justifyContent: 'start',
            marginTop: 1.5,
            width: 'fit-content',
          }}
        >
          + Group
        </Button>
      ) : undefined}
    </>
  );
};
